import { useState, useEffect, useRef } from 'react';
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send, Users } from 'lucide-react';
import { HubChat, TypingIndicator } from './types';
import { getMemberDisplayName } from '@/utils/memberUtils';
import { awardXP, getXPForAction } from '@/utils/gamificationUtils';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface RealTimeChatProps {
  hubId: string;
  hubName: string;
}

export default function RealTimeChat({ hubId, hubName }: RealTimeChatProps) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<HubChat[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [typingUsers, setTypingUsers] = useState<TypingIndicator[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    fetchMessages();
    setupRealtimeSubscriptions();
    
    return () => {
      clearTimeout(typingTimeoutRef.current);
    };
  }, [hubId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('hub_chats')
        .select('*')
        .eq('hub_id', hubId)
        .order('created_at', { ascending: true })
        .limit(100);
        
      if (error) throw error;
      
      // Fetch author names for messages and ensure proper typing
      const messagesWithAuthors = await Promise.all(
        (data || []).map(async (message) => {
          const authorName = await getMemberDisplayName(message.user_id);
          return {
            ...message,
            message_type: message.message_type as 'text' | 'image' | 'emoji_reaction',
            author: {
              id: message.user_id,
              name: authorName,
              avatar: null
            }
          } as HubChat;
        })
      );
      
      setMessages(messagesWithAuthors);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    // Subscribe to new messages
    const messagesChannel = supabase
      .channel(`hub_chats_${hubId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'hub_chats',
        filter: `hub_id=eq.${hubId}`
      }, async (payload) => {
        const newMessage = payload.new as any;
        const authorName = await getMemberDisplayName(newMessage.user_id);
        
        const formattedMessage: HubChat = {
          ...newMessage,
          message_type: newMessage.message_type as 'text' | 'image' | 'emoji_reaction',
          author: {
            id: newMessage.user_id,
            name: authorName,
            avatar: null
          }
        };
        
        setMessages(prev => [...prev, formattedMessage]);
      })
      .subscribe();

    // Subscribe to typing indicators
    const typingChannel = supabase
      .channel(`typing_${hubId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'typing_indicators',
        filter: `hub_id=eq.${hubId}`
      }, async (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          const indicator = payload.new as any;
          if (indicator.user_id !== user?.id && indicator.is_typing) {
            const authorName = await getMemberDisplayName(indicator.user_id);
            const typingIndicator: TypingIndicator = {
              ...indicator,
              author: { name: authorName }
            };
            setTypingUsers(prev => {
              const filtered = prev.filter(t => t.user_id !== indicator.user_id);
              return [...filtered, typingIndicator];
            });
          }
        } else if (payload.eventType === 'DELETE') {
          const indicator = payload.old as any;
          setTypingUsers(prev => prev.filter(t => t.user_id !== indicator.user_id));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(typingChannel);
    };
  };

  const handleTyping = async () => {
    if (!user) return;
    
    try {
      // Update typing indicator
      await supabase
        .from('typing_indicators')
        .upsert({
          hub_id: hubId,
          user_id: user.id,
          is_typing: true,
          expires_at: new Date(Date.now() + 10000).toISOString()
        });
      
      // Clear previous timeout and set new one
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(async () => {
        await supabase
          .from('typing_indicators')
          .delete()
          .eq('hub_id', hubId)
          .eq('user_id', user.id);
      }, 8000);
    } catch (error) {
      console.error('Error updating typing indicator:', error);
    }
  };

  const sendMessage = async () => {
    if (!user || !newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const { error } = await supabase
        .from('hub_chats')
        .insert({
          hub_id: hubId,
          user_id: user.id,
          message: newMessage.trim(),
          message_type: 'text'
        });
        
      if (error) throw error;
      
      // Award XP for sending message
      await awardXP(user.id, getXPForAction('send_chat'), 'send_chat');
      
      setNewMessage('');
      
      // Clear typing indicator
      await supabase
        .from('typing_indicators')
        .delete()
        .eq('hub_id', hubId)
        .eq('user_id', user.id);
        
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="w-6 h-6 border-2 border-omoduduwa-terracotta border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-96 bg-white rounded-lg border">
      {/* Chat header */}
      <div className="flex items-center justify-between p-3 border-b bg-omoduduwa-terracotta text-white rounded-t-lg">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" />
          <span className="font-medium">{hubName} Chat</span>
        </div>
        <span className="text-sm opacity-90">{messages.length} messages</span>
      </div>

      {/* Messages area */}
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-3">
          {messages.map((message) => (
            <div key={message.id} className="flex gap-2">
              <Avatar className="w-8 h-8">
                <AvatarImage src={message.author?.avatar || undefined} />
                <AvatarFallback className="text-xs bg-omoduduwa-terracotta text-white">
                  {message.author?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{message.author?.name}</span>
                  <span className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-sm text-gray-700 break-words">{message.message}</p>
              </div>
            </div>
          ))}
          
          {/* Typing indicators */}
          {typingUsers.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-500 italic">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-omoduduwa-terracotta rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-omoduduwa-terracotta rounded-full animate-bounce delay-100"></div>
                <div className="w-2 h-2 bg-omoduduwa-terracotta rounded-full animate-bounce delay-200"></div>
              </div>
              <span>
                {typingUsers.map(t => t.author?.name).join(', ')} 
                {typingUsers.length === 1 ? ' is' : ' are'} typing...
              </span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Message input */}
      <div className="p-3 border-t">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="flex-1"
            disabled={!user || sending}
          />
          <Button 
            onClick={sendMessage}
            disabled={!user || !newMessage.trim() || sending}
            className="bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
